/**
 * 登录页。手机验证码，没有密码。
 *
 * 旧管理端 `Admin` 表就没有密码字段，新系统沿用。
 * `NODE_ENV=development` 时用户端与管理端任意 6 位数字都通过，
 * 接口会返回 `environment: "development"`，这里据此自动填充并给出明确提示。
 */

import { useState } from "react";
import { Alert, App, Button, Card, Form, Input, Space, Typography } from "antd";
import { LockOutlined, MobileOutlined } from "@ant-design/icons";
import { authApi } from "../../services/api";
import { useAuth } from "../../services/auth";

export function LoginPage() {
  const { login } = useAuth();
  const { message } = App.useApp();
  const [form] = Form.useForm<{ phone: string; code: string }>();
  const [isDevelopment, setIsDevelopment] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const sendCode = async () => {
    try {
      const phone = (await form.validateFields(["phone"])).phone;
      setSending(true);
      const res = await authApi.requestCode(phone);
      if (res.environment === "development") {
        setIsDevelopment(true);
        form.setFieldValue("code", res.devCode ?? "000000");
        message.info("开发环境：任意 6 位数字都可通过，已自动填充");
      } else {
        message.success("验证码已发送");
      }
    } catch (e) {
      if (e instanceof Error && e.message) message.error(e.message);
    } finally {
      setSending(false);
    }
  };

  const submit = async (values: { phone: string; code: string }) => {
    setSubmitting(true);
    try {
      await login(values.phone, values.code);
    } catch (e) {
      message.error(e instanceof Error ? e.message : "登录失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "grid", placeItems: "center", height: "100vh", background: "var(--miao-color-canvas)" }}>
      <Card style={{ width: 380 }} styles={{ body: { padding: 28 } }}>
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <div>
            <Typography.Title level={4} style={{ margin: 0, color: "var(--miao-brand-700)" }}>
              梦灵管理端
            </Typography.Title>
            <Typography.Text type="secondary">内部运营与排障，登录即被审计</Typography.Text>
          </div>

          {isDevelopment && (
            <Alert
              type="warning"
              showIcon
              message="当前为开发环境"
              description="任意 6 位数字都能登录；将 apps/api/.env 的 NODE_ENV 切到 production 后会统一使用真实验证码。"
            />
          )}

          <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
            <Form.Item
              name="phone"
              label="手机号"
              rules={[{ required: true, pattern: /^1[3-9]\d{9}$/, message: "请输入 11 位手机号" }]}
            >
              <Input prefix={<MobileOutlined />} placeholder="管理员手机号" maxLength={11} autoFocus />
            </Form.Item>

            <Form.Item
              name="code"
              label="验证码"
              rules={[{ required: true, pattern: /^\d{6}$/, message: "6 位数字验证码" }]}
            >
              <Space.Compact style={{ width: "100%" }}>
                <Input prefix={<LockOutlined />} placeholder="6 位验证码" maxLength={6} />
                <Button onClick={sendCode} loading={sending}>
                  获取验证码
                </Button>
              </Space.Compact>
            </Form.Item>

            <Button type="primary" htmlType="submit" block loading={submitting}>
              登录
            </Button>
          </Form>

          <Typography.Link
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noreferrer"
            style={{ display: "block", textAlign: "center", fontSize: 12 }}
          >
            浙ICP备2022025027号-6
          </Typography.Link>
        </Space>
      </Card>
    </div>
  );
}
